import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EnhancedTestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);

  // Form state
  const [formData, setFormData] = useState(getEmptyFormData());

  function getEmptyFormData() {
    return {
      name: '',
      location: '',
      content: '',
      rating: 5,
      image: '',
      adventure_id: '',
      featured: false,
      sort_order: 0,
      is_active: true
    };
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([
      fetchTestimonials(),
      fetchAdventures()
    ]);
  };

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/testimonials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTestimonials(data.testimonials || []);
      } else {
        setError('Failed to fetch testimonials');
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdventures = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/adventures`);
      if (response.ok) {
        const adventures = await response.json();
        setAdventures(adventures);
      }
    } catch (err) {
      console.error('Error fetching adventures:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    clearMessages();
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setSelectedTestimonial(null);
    setIsCreating(false);
    setShowImageUpload(false);
  };

  const startCreating = () => {
    resetForm();
    setIsCreating(true);
  };

  const startEditing = (testimonial) => {
    setFormData({
      name: testimonial.name || '',
      location: testimonial.location || '',
      content: testimonial.content || '',
      rating: testimonial.rating || 5,
      image: testimonial.image || '',
      adventure_id: testimonial.adventure_id || '',
      featured: testimonial.featured || false,
      sort_order: testimonial.sort_order || 0,
      is_active: testimonial.is_active !== false
    });
    setSelectedTestimonial(testimonial);
    setIsCreating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.content) {
      setError('Name and testimonial content are required');
      return;
    }

    setSaving(true);
    clearMessages();

    try {
      const token = localStorage.getItem('adminToken');
      const url = selectedTestimonial 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/testimonials/${selectedTestimonial.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/testimonials`;
      
      const method = selectedTestimonial ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(selectedTestimonial ? 'Testimonial updated successfully!' : 'Testimonial created successfully!');
        await fetchTestimonials();
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save testimonial');
      }
    } catch (err) {
      console.error('Error saving testimonial:', err);
      setError('Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/testimonials/${testimonialId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Testimonial deleted successfully!');
        await fetchTestimonials();
        if (selectedTestimonial?.id === testimonialId) {
          resetForm();
        }
      } else {
        setError('Failed to delete testimonial');
      }
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      setError('Failed to delete testimonial');
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const formData_upload = new FormData();
    formData_upload.append('file', file);
    formData_upload.append('category', 'testimonials');

    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData_upload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        setSuccess('Image uploaded successfully!');
      } else {
        setError('Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    } finally {
      setSaving(false);
      setShowImageUpload(false);
    }
  };

  const getAdventureName = (adventureId) => {
    if (!adventureId) return 'General';
    const adventure = adventures.find(a => a.id === adventureId);
    return adventure ? adventure.title : 'Unknown Adventure';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500"></div>
        <span className="ml-3 text-slate-600">Loading testimonials...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Testimonials Manager</h2>
          <p className="text-slate-600 mt-1">Manage customer testimonials and reviews</p>
        </div>
        <button
          onClick={startCreating}
          className="bg-coral-500 hover:bg-coral-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          + Add New Testimonial
        </button>
      </div>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
          >
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg"
          >
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Testimonials List */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            All Testimonials ({testimonials.length})
          </h3>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedTestimonial?.id === testimonial.id
                    ? 'border-coral-500 bg-coral-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => startEditing(testimonial)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    {testimonial.image && (
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-8 h-8 rounded-full object-cover mr-3"
                      />
                    )}
                    <div>
                      <h4 className="font-medium text-slate-800">{testimonial.name}</h4>
                      <p className="text-xs text-slate-500">{testimonial.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {testimonial.featured && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(testimonial.id);
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                  "{testimonial.content}"
                </p>
                
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>★ {testimonial.rating}/5</span>
                  <span>{getAdventureName(testimonial.adventure_id)}</span>
                </div>
              </div>
            ))}
            
            {testimonials.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <div className="text-4xl mb-2">💬</div>
                <p>No testimonials yet</p>
                <p className="text-sm">Add your first customer testimonial</p>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || selectedTestimonial) && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {isCreating ? 'Add New Testimonial' : 'Edit Testimonial'}
              </h3>
              <button
                onClick={resetForm}
                className="text-slate-500 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Sydney, Australia"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Testimonial Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200 resize-none"
                  placeholder="Enter the customer's testimonial..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Rating
                  </label>
                  <select
                    value={formData.rating}
                    onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200"
                  >
                    {[5, 4, 3, 2, 1].map(rating => (
                      <option key={rating} value={rating}>
                        ★ {rating} Star{rating !== 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Adventure Type
                  </label>
                  <select
                    value={formData.adventure_id}
                    onChange={(e) => handleInputChange('adventure_id', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200"
                  >
                    <option value="">General Testimonial</option>
                    {adventures.map((adventure) => (
                      <option key={adventure.id} value={adventure.id}>
                        {adventure.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reviewer Photo */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reviewer Photo
                </label>
                
                {formData.image ? (
                  <div className="flex items-center space-x-4">
                    <img 
                      src={formData.image} 
                      alt="Reviewer" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                    />
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowImageUpload(true)}
                        className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1 rounded"
                      >
                        Change Photo
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInputChange('image', '')}
                        className="text-sm text-red-500 hover:text-red-700 ml-2"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {showImageUpload ? (
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleImageUpload(file);
                          }}
                          className="w-full"
                        />
                        <button
                          type="button"
                          onClick={() => setShowImageUpload(false)}
                          className="text-sm text-slate-500 mt-2"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowImageUpload(true)}
                        className="border-2 border-dashed border-slate-300 rounded-lg p-4 w-full text-slate-500 hover:border-slate-400 transition-colors"
                      >
                        📷 Click to add reviewer photo
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => handleInputChange('featured', e.target.checked)}
                      className="rounded border-slate-300 text-coral-500 focus:ring-coral-200"
                    />
                    <span className="ml-2 text-sm text-slate-700">Featured testimonial</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-coral-500 focus:ring-2 focus:ring-coral-200"
                    min="0"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-coral-500 hover:bg-coral-600 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {saving ? 'Saving...' : (isCreating ? 'Create Testimonial' : 'Update Testimonial')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedTestimonialsManager;